import InfoCircle from 'assets/icons/InfoCircle';
import Warning from 'assets/icons/Warning';
import CloseCircle from 'assets/icons/CloseCircle';
import CheckMark from 'assets/icons/CheckMark';
import Gear from 'assets/icons/Gear';
import COLOR from 'constants/color';
import { FC, ReactNode } from 'react';

export type BannerSize = 'small' | 'medium' | 'large';
export type BannerVariant =
  | 'info'
  | 'warning'
  | 'error'
  | 'danger'
  | 'success'
  | 'neutral';

export interface BannerProps {
  message: string;
  icon?: ReactNode;
  /** Optional buttons/links rendered after the message. */
  actions?: ReactNode;
  size?: BannerSize;
  variant?: BannerVariant;
  className?: string;
  fullWidth?: boolean;
  dismissible?: boolean;
  onDismiss?: () => void;
  dismissAriaLabel?: string;
}

const sizeClasses = {
  small: {
    container: '~p-1.5/2 ~gap-1.5/2',
    text: '~text-[0.625rem]/xs',
    icon: '~w-3.5/4 ~h-3.5/4',
    actions: '~gap-1/1.5'
  },
  medium: {
    container: '~p-2/3 ~gap-2/3',
    text: '~text-xs/sm',
    icon: '~w-4/5 ~h-4/5',
    actions: '~gap-1.5/2'
  },
  large: {
    container: '~p-3/4 ~gap-3/4',
    text: '~text-sm/base',
    icon: '~w-5/6 ~h-5/6',
    actions: '~gap-2/3'
  }
} as const;

/** High-contrast fills/borders/ink for light + dark (theme tokens where possible). */
const variantClasses = {
  info: {
    bg: 'bg-blue-50 dark:bg-blue-950/55',
    border: 'border-blue-300 dark:border-blue-500',
    text: 'text-blue-950 dark:text-blue-50'
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-950/45',
    border: 'border-amber-400 dark:border-amber-500',
    text: 'text-amber-950 dark:text-amber-50'
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-950/50',
    border: 'border-red-300 dark:border-red-500',
    text: 'text-red-950 dark:text-red-50'
  },
  danger: {
    bg: 'bg-red-50 dark:bg-red-950/50',
    border: 'border-red-300 dark:border-red-500',
    text: 'text-red-950 dark:text-red-50'
  },
  success: {
    bg: 'bg-green-50 dark:bg-green-950/45',
    border: 'border-green-300 dark:border-green-500',
    text: 'text-green-950 dark:text-green-50'
  },
  neutral: {
    bg: 'bg-gray-100 dark:bg-secondary-dark-300',
    border: 'border-gray-300 dark:border-secondary-dark-500',
    text: 'text-gray-900 dark:text-secondary-dark-900'
  }
} as const;

const BannerBase: FC<BannerProps> = ({
  message,
  icon,
  actions,
  size = 'medium',
  variant = 'info',
  className = '',
  fullWidth = true,
  dismissible = false,
  onDismiss,
  dismissAriaLabel = 'Dismiss'
}) => {
  const currentSize = sizeClasses[size];
  const currentVariant = variantClasses[variant];

  const defaultIcons = {
    info: (
      <InfoCircle
        styles={currentSize.icon}
        fill={COLOR.SEMANTIC.INFO_STRONG}
        fillDarkMode={COLOR.SEMANTIC.INFO_SOFT}
      />
    ),
    warning: (
      <Warning
        styles={currentSize.icon}
        fill={COLOR.SEMANTIC.WARNING}
        fillDarkMode={COLOR.PRIMARY.DARK_600}
      />
    ),
    error: (
      <CloseCircle
        styles={currentSize.icon}
        fill={COLOR.SEMANTIC.ERROR}
        fillDarkMode={COLOR.SEMANTIC.ERROR_SOFT}
      />
    ),
    danger: (
      <CloseCircle
        styles={currentSize.icon}
        fill={COLOR.SEMANTIC.ERROR}
        fillDarkMode={COLOR.SEMANTIC.ERROR_SOFT}
      />
    ),
    success: (
      <CheckMark
        styles={currentSize.icon}
        fill={COLOR.SEMANTIC.SUCCESS}
        fillDarkMode={COLOR.SEMANTIC.SUCCESS}
      />
    ),
    neutral: (
      <Gear
        styles={currentSize.icon}
        fill={COLOR.GRAY['700']}
        fillDarkMode={COLOR.GRAY['500']}
      />
    )
  };

  const displayIcon = icon || defaultIcons[variant];

  return (
    <div
      role='status'
      aria-live='polite'
      className={`${fullWidth ? 'w-full' : 'w-fit'} ${currentSize.container} rounded-md ${currentVariant.bg} border ${currentVariant.border} flex items-center ${className}`}>
      <div className={`${currentSize.icon} flex-shrink-0`}>{displayIcon}</div>
      <p
        className={`${currentSize.text} ${currentVariant.text} flex-1 font-medium min-w-0`}>
        {message}
      </p>
      {actions ? (
        <div
          className={`flex flex-wrap items-center flex-shrink-0 ${currentSize.actions}`}>
          {actions}
        </div>
      ) : null}
      {dismissible && onDismiss ? (
        <button
          type='button'
          onClick={onDismiss}
          aria-label={dismissAriaLabel}
          className={`flex-shrink-0 ~ml-1/2 ~p-0.5/1 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors`}>
          <CloseCircle
            styles={currentSize.icon}
            fill={COLOR.GRAY['700']}
            fillDarkMode={COLOR.GRAY['500']}
          />
        </button>
      ) : null}
    </div>
  );
};

export const BannerSmall = (props: Omit<BannerProps, 'size'>) => (
  <BannerBase {...props} size='small' />
);

export const BannerMedium = (props: Omit<BannerProps, 'size'>) => (
  <BannerBase {...props} size='medium' />
);

export const BannerLarge = (props: Omit<BannerProps, 'size'>) => (
  <BannerBase {...props} size='large' />
);

export const InfoBanner = (props: Omit<BannerProps, 'variant'>) => (
  <BannerBase {...props} variant='info' />
);

export const WarningBanner = (props: Omit<BannerProps, 'variant'>) => (
  <BannerBase {...props} variant='warning' />
);

export const ErrorBanner = (props: Omit<BannerProps, 'variant'>) => (
  <BannerBase {...props} variant='error' />
);

export const SuccessBanner = (props: Omit<BannerProps, 'variant'>) => (
  <BannerBase {...props} variant='success' />
);

export const DangerBanner = (props: Omit<BannerProps, 'variant'>) => (
  <BannerBase {...props} variant='danger' />
);

export const NeutralBanner = (props: Omit<BannerProps, 'variant'>) => (
  <BannerBase {...props} variant='neutral' />
);

const Banner: React.FC<BannerProps> = (props) => (
  <BannerBase
    {...props}
    size={props.size || 'medium'}
    variant={props.variant || 'info'}
  />
);

export default Banner;
